import React, { useEffect, useState } from 'react';
import { PlusIcon, SaveIcon, Trash2Icon } from 'lucide-react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { BillingSettings, BillingTaxTemplate } from '../../types';
type SettingsDraft = Pick<BillingSettings, 'currency' | 'invoicePrefix' | 'receiptPrefix' | 'quotationPrefix' | 'creditNotePrefix' | 'paymentTerms' | 'taxTemplates'>;
export function Settings() {
  const {
    billingSettings,
    currentUser,
    updateBillingSettings
  } = useData();
  const [draft, setDraft] = useState<SettingsDraft>(toDraft(billingSettings));
  const [status, setStatus] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  useEffect(() => {
    setDraft(toDraft(billingSettings));
  }, [billingSettings]);
  if (currentUser?.role !== 'Administrator') {
    return <div className="rounded-lg border border-red-200 bg-red-50 p-8 text-center text-red-700" role="alert" data-id="element-1924">
        Only Administrators can manage Billing Settings and tax templates.
      </div>;
  }
  const updateTemplate = (id: string, patch: Partial<BillingTaxTemplate>) => {
    setDraft(current => ({
      ...current,
      taxTemplates: current.taxTemplates.map(template => template.id === id ? {
        ...template,
        ...patch
      } : template)
    }));
  };
  const addTemplate = () => {
    setDraft(current => ({
      ...current,
      taxTemplates: [...current.taxTemplates, {
        id: `tax-${Date.now()}`,
        name: 'New tax template',
        type: 'Charge',
        rate: 0,
        isActive: true
      }]
    }));
  };
  const removeTemplate = (id: string) => {
    if (draft.taxTemplates.length <= 1) {
      setStatus({
        type: 'error',
        message: 'Keep at least one tax template available for invoice lines.'
      });
      return;
    }
    setDraft(current => ({
      ...current,
      taxTemplates: current.taxTemplates.filter(template => template.id !== id)
    }));
  };
  const saveSettings = async () => {
    const validTemplates = draft.taxTemplates.map(template => ({
      ...template,
      name: template.name.trim(),
      rate: template.type === 'Exempt' ? 0 : Math.min(100, Math.max(0, Number(template.rate) || 0))
    }));
    if (validTemplates.some(template => !template.name)) {
      setStatus({
        type: 'error',
        message: 'Each tax template needs a name.'
      });
      return;
    }
    if (!validTemplates.some(template => template.type === 'Exempt' && template.rate === 0)) {
      setStatus({
        type: 'error',
        message: 'Keep a 0% Tax Exempt template available.'
      });
      return;
    }
    const result = await updateBillingSettings({
      ...draft,
      taxTemplates: validTemplates
    });
    setStatus(result.ok ? {
      type: 'success',
      message: 'Billing settings and tax templates saved.'
    } : {
      type: 'error',
      message: result.error || 'Billing settings could not be saved.'
    });
  };
  return <div className="max-w-5xl space-y-6" data-id="element-1925">
      <div data-id="element-1926">
        <p className="text-sm font-semibold text-pink-600" data-id="element-1927">
          Billing administration
        </p>
        <h2 className="mt-1 text-2xl font-bold text-slate-950" data-id="element-1928">
          Billing Settings
        </h2>
        <p className="mt-1 text-sm text-slate-600" data-id="element-1929">
          Tax templates are applied to invoice lines after discount. Deductions
          reduce the invoice total.
        </p>
      </div>

      {status && <div role="status" className={`rounded-lg border px-4 py-3 text-sm ${status.type === 'success' ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-red-200 bg-red-50 text-red-700'}`} data-id="element-1930">
          {status.message}
        </div>}

      <Card title="General Preferences" data-id="element-1931">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2" data-id="element-1932">
          <Select label="Base Currency" value={draft.currency} onChange={event => setDraft(current => ({
          ...current,
          currency: event.target.value as BillingSettings['currency']
        }))} options={[{
          value: 'ZMW',
          label: 'Zambian Kwacha (ZMW)'
        }, {
          value: 'USD',
          label: 'US Dollar (USD)'
        }]} data-id="element-1933" />
          <Select label="Default Payment Terms" value={draft.paymentTerms} onChange={event => setDraft(current => ({
          ...current,
          paymentTerms: event.target.value
        }))} options={[{
          value: 'Due on Receipt',
          label: 'Due on Receipt'
        }, {
          value: 'Net 7',
          label: 'Net 7'
        }, {
          value: 'Net 15',
          label: 'Net 15'
        }, {
          value: 'Net 30',
          label: 'Net 30'
        }]} data-id="element-1934" />
        </div>
      </Card>

      <Card noPadding data-id="element-1935">
        <div className="flex flex-col gap-3 border-b border-slate-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-1936">
          <div data-id="element-1937">
            <h3 className="font-bold text-slate-900" data-id="element-1938">Tax templates</h3>
            <p className="mt-0.5 text-sm text-slate-500" data-id="element-1939">
              Charge adds to the total; deduction reduces it; exemption is
              retained as a 0% audit choice.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={addTemplate} data-id="element-1940">
            <PlusIcon className="mr-1.5 h-4 w-4" data-id="element-1941" /> Add template
          </Button>
        </div>
        <div className="overflow-x-auto" data-id="element-1942">
          <table className="min-w-[740px] w-full divide-y divide-slate-200" data-id="element-1943">
            <thead className="bg-slate-50" data-id="element-1944">
              <tr data-id="element-1945">
                <th scope="col" className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-1946">
                  Template
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-1947">
                  Treatment
                </th>
                <th scope="col" className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-1948">
                  Rate
                </th>
                <th scope="col" className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-1949">
                  Available
                </th>
                <th scope="col" className="w-16 px-4 py-3" data-id="element-1950">
                  <span className="sr-only" data-id="element-1951">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white" data-id="element-1952">
              {draft.taxTemplates.map(template => <tr key={template.id} data-id="element-1953">
                  <td className="px-5 py-3" data-id="element-1954">
                    <label className="sr-only" htmlFor={`tax-name-${template.id}`} data-id="element-1955">
                      Tax template name
                    </label>
                    <input id={`tax-name-${template.id}`} value={template.name} onChange={event => updateTemplate(template.id, {
                  name: event.target.value
                })} className="w-full rounded border border-slate-300 px-2.5 py-1.5 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" data-id="element-1956" />
                  </td>
                  <td className="px-4 py-3" data-id="element-1957">
                    <label className="sr-only" htmlFor={`tax-type-${template.id}`} data-id="element-1958">
                      Tax template type
                    </label>
                    <select id={`tax-type-${template.id}`} value={template.type} onChange={event => {
                  const type = event.target.value as BillingTaxTemplate['type'];
                  updateTemplate(template.id, {
                    type,
                    ...(type === 'Exempt' ? {
                      rate: 0
                    } : {})
                  });
                }} className="w-full rounded border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" data-id="element-1959">
                      <option value="Charge" data-id="element-1960">Charge</option>
                      <option value="Deduction" data-id="element-1961">
                        Medical Scheme deduction
                      </option>
                      <option value="Exempt" data-id="element-1962">Tax Exempt</option>
                    </select>
                  </td>
                  <td className="px-4 py-3" data-id="element-1963">
                    <label className="sr-only" htmlFor={`tax-rate-${template.id}`} data-id="element-1964">
                      Tax template rate
                    </label>
                    <div className="relative ml-auto w-28" data-id="element-1965">
                      <input id={`tax-rate-${template.id}`} type="number" min="0" max="100" step="0.01" disabled={template.type === 'Exempt'} value={template.rate} onChange={event => updateTemplate(template.id, {
                    rate: Number(event.target.value) || 0
                  })} className="w-full rounded border border-slate-300 px-2.5 py-1.5 pr-7 text-right text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100" data-id="element-1966" />
                      <span className="pointer-events-none absolute right-2 top-1.5 text-xs text-slate-500" data-id="element-1967">
                        %
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center" data-id="element-1968">
                    <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700" data-id="element-1969">
                      <input type="checkbox" checked={template.isActive} onChange={event => updateTemplate(template.id, {
                    isActive: event.target.checked
                  })} className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" data-id="element-1970" />
                      Active
                    </label>
                  </td>
                  <td className="px-4 py-3 text-center" data-id="element-1971">
                    <button type="button" onClick={() => removeTemplate(template.id)} aria-label={`Remove ${template.name || 'tax template'}`} className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500" data-id="element-1972">
                      <Trash2Icon className="h-4 w-4" data-id="element-1973" />
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="Numbering Prefixes" data-id="element-1974">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2" data-id="element-1975">
          <Input label="Invoice Prefix" value={draft.invoicePrefix} onChange={event => setDraft(current => ({
          ...current,
          invoicePrefix: event.target.value
        }))} data-id="element-1976" />
          <Input label="Receipt Prefix" value={draft.receiptPrefix} onChange={event => setDraft(current => ({
          ...current,
          receiptPrefix: event.target.value
        }))} data-id="element-1977" />
          <Input label="Quotation Prefix" value={draft.quotationPrefix} onChange={event => setDraft(current => ({
          ...current,
          quotationPrefix: event.target.value
        }))} data-id="element-1978" />
          <Input label="Credit Note Prefix" value={draft.creditNotePrefix} onChange={event => setDraft(current => ({
          ...current,
          creditNotePrefix: event.target.value
        }))} data-id="element-1979" />
        </div>
      </Card>

      <div className="flex justify-end" data-id="element-1980">
        <Button onClick={saveSettings} data-id="element-1981">
          <SaveIcon className="mr-1.5 h-4 w-4" data-id="element-1982" /> Save Billing Settings
        </Button>
      </div>
    </div>;
}
function toDraft(settings: BillingSettings): SettingsDraft {
  return {
    currency: settings.currency,
    invoicePrefix: settings.invoicePrefix,
    receiptPrefix: settings.receiptPrefix,
    quotationPrefix: settings.quotationPrefix,
    creditNotePrefix: settings.creditNotePrefix,
    paymentTerms: settings.paymentTerms,
    taxTemplates: settings.taxTemplates.map(template => ({
      ...template
    }))
  };
}